// All actions and state related to a User's Groups

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createItemOnServer, deleteItemOnServer } from "../api/api";

// Async thunk to add a group
export const addGroupToReduxAndServer = createAsyncThunk(
    'groups/addGroup',
    async (groupData, thunkAPI) => {
        try {
            // Send a POST request to create the group on the server
            const newGroup = await createItemOnServer('groups', groupData);
            return newGroup;
        } catch (error) {
            // Return the rejected promise with the error message
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// Async thunk to update group
export const updateGroupOnReduxAndServer = createAsyncThunk(
    'groups/updateGroup',
    async ({ id, updatedGroup }, thunkAPI) => {
        try {
            const updated = await createItemOnServer('groups', updatedGroup);
            return { id, updatedGroup: updated };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// Async thunk to remove a group
export const removeGroupFromReduxAndServer = createAsyncThunk(
    'groups/removeGroup',
    async (groupID, thunkAPI) => {
        try {
            // Send a DELETE request to remove the group from the server
            await deleteItemOnServer('groups', groupID);
            return groupID;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

const initialState = {
    groups: [],
    tempGroupName: "",
    tempGroupHearts: [],
    tempGroupID: "",
};

export const groupSlice = createSlice({
    name: "group",
    initialState,
    reducers: {
        addGroup: (state, action) => {
            state.groups.push(action.payload);
        },
        updateGroup: (state, action) => {
            const { id, updatedGroup } = action.payload;
            state.groups = state.groups.map((group) => group.id === id ? updatedGroup : group);
        },
        updateTempGroupName: (state, action) => {
            state.tempGroupName = action.payload;
        },
        updateTempGroupHearts: (state, action) => {
            state.tempGroupHearts = action.payload;
        },
        updateTempGroupID: (state, action) => {
            state.tempGroupID = action.payload;
        },
        clearReduxGroups: (state, action) => {
            state.groups = [];
            state.tempGroupName = "";
            state.tempGroupHearts = [];
            state.tempGroupID = "";
        },
        removeGroup: (state, action) => {
            state.groups = state.groups.filter((group) => group.id !== action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(addGroupToReduxAndServer.fulfilled, (state, action) => {
                state.groups.push(action.payload);
            })
            .addCase(addGroupToReduxAndServer.pending, (state, action) => {
                console.log("Adding group to server...");
            })
            .addCase(addGroupToReduxAndServer.rejected, (state, action) => {
                console.error("Error adding group to server: ", action.payload);
            })
            .addCase(updateGroupOnReduxAndServer.fulfilled, (state, action) => {
                const { id, updatedGroup } = action.payload;
                state.groups = state.groups.map((group) => group.id === id ? updatedGroup : group);
            })
            .addCase(updateGroupOnReduxAndServer.pending, (state, action) => {
                console.log("Updating group on server...");
            })
            .addCase(updateGroupOnReduxAndServer.rejected, (state, action) => {
                console.error("Error updating group on server: ", action.payload);
            })
            .addCase(removeGroupFromReduxAndServer.fulfilled, (state, action) => {
                state.groups = state.groups.filter((group) => group.id !== action.payload);
            })
            .addCase(removeGroupFromReduxAndServer.pending, (state, action) => {
                console.log("Removing group from server...");
            })
            .addCase(removeGroupFromReduxAndServer.rejected, (state, action) => {
                console.error("Error removing group from server: ", action.payload);
            });
    },
});

// export the actions
export const { addGroup, updateTempGroupHearts, updateTempGroupName, updateTempGroupID, updateGroup, clearReduxGroups, removeGroup } = groupSlice.actions;

// export the reducer
export default groupSlice.reducer;